import React from 'react';
import { 
  MessageSquare, 
  Database, 
  Target,
  MessageCircle,
  Type,
  Zap,
  Info,
  BarChart3,
  GitBranch
} from 'lucide-react';

const NodePalette = () => {
  const onDragStart = (event, nodeType) => {
    event.dataTransfer.setData('application/reactflow', nodeType);
    event.dataTransfer.effectAllowed = 'move';
  };

  const nodeTypes = [
    {
      type: 'textFieldsNode',
      label: 'TextFields Node',
      description: 'Define custom variables with multiple values for prompt templates',
      icon: Type,
      color: 'gray'
    },
    {
      type: 'promptNode',
      label: 'Prompt Node',
      description: 'Send prompts to LLMs and process responses',
      icon: MessageSquare,
      color: 'celadon'
    },
    {
      type: 'testSetNode',
      label: 'Test Set Node',
      description: 'Input test cases for evaluation',
      icon: Database,
      color: 'gray'
    },
    {
      type: 'autoEvaluatorNode',
      label: 'Auto-Evaluator',
      description: 'Evaluate LLM responses based on custom criteria',
      icon: Target,
      color: 'celadon'
    },
    {
      type: 'responseNode',
      label: 'Response Display',
      description: 'Display and preview LLM responses',
      icon: MessageCircle,
      color: 'gray'
    },
    {
      type: 'visualizerNode',
      label: 'Visualizer Node',
      description: 'Create tables and charts from connected node data',
      icon: BarChart3,
      color: 'blue'
    },
  ];

  return (
    <div className="h-full bg-gray-50">
      {/* Header */}
      <div className="card-header">
        <div className="flex items-center space-x-3">
          <div className="p-2 bg-gradient-to-br from-celadon-50 to-celadon-100 rounded-xl">
            <Zap className="h-5 w-5 text-celadon-700" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Node Palette</h3>
            <p className="text-sm text-gray-600">Drag components to canvas</p>
          </div>
        </div>
      </div>

      {/* Node Types */}
      <div className="p-6 space-y-4">
        {nodeTypes.map((nodeType) => {
          const IconComponent = nodeType.icon;
          return (
            <div
              key={nodeType.type}
              className="card cursor-grab active:cursor-grabbing hover:shadow-medium transition-all duration-200 group border-2 border-dashed border-gray-200 hover:border-celadon-300"
              draggable
              onDragStart={(event) => onDragStart(event, nodeType.type)}
            >
              <div className="card-body !p-4">
                <div className="flex items-start space-x-3">
                  <div className={`
                    p-3 rounded-xl transition-colors
                    ${nodeType.color === 'celadon' 
                      ? 'bg-celadon-100 group-hover:bg-celadon-200' 
                      : nodeType.color === 'blue'
                      ? 'bg-blue-100 group-hover:bg-blue-200'
                      : nodeType.color === 'purple'
                      ? 'bg-purple-100 group-hover:bg-purple-200'
                      : 'bg-gray-100 group-hover:bg-gray-200'
                    }
                  `}>
                    <IconComponent className={`h-5 w-5 ${
                      nodeType.color === 'celadon' 
                        ? 'text-celadon-700' 
                        : nodeType.color === 'blue'
                        ? 'text-blue-700'
                        : nodeType.color === 'purple'
                        ? 'text-purple-700'
                        : 'text-gray-700'
                    }`} />
                  </div>
                  
                  <div className="flex-1 min-w-0">
                    <h4 className="text-sm font-semibold text-gray-900 mb-1">
                      {nodeType.label}
                    </h4>
                    <p className="text-xs text-gray-600 leading-relaxed">
                      {nodeType.description}
                    </p>
                  </div>
                </div>
                

              </div>
            </div>
          );
        })}
      </div>
      
        
        {/* Legend */}
        <div className="mt-4 space-y-2">
          <div className="flex items-center text-xs text-gray-600">
            <div className="w-3 h-3 bg-gray-100 rounded mr-3 flex items-center justify-center">
              <Type className="h-2 w-2 text-gray-700" />
            </div>
            <span>Input & utility nodes</span>
          </div>
          <div className="flex items-center text-xs text-gray-600">
            <div className="w-3 h-3 bg-celadon-100 rounded mr-3 flex items-center justify-center">
              <MessageSquare className="h-2 w-2 text-celadon-700" />
            </div>
            <span>AI processing nodes</span>
          </div>
          <div className="flex items-center text-xs text-gray-600">
            <div className="w-3 h-3 bg-blue-100 rounded mr-3 flex items-center justify-center">
              <BarChart3 className="h-2 w-2 text-blue-700" />
            </div>
            <span>Visualization nodes</span>
          </div>
          <div className="flex items-center text-xs text-gray-600">
            <div className="w-3 h-3 bg-purple-100 rounded mr-3 flex items-center justify-center">
              <GitBranch className="h-2 w-2 text-purple-700" />
            </div>
            <span>Flow control nodes</span>
          </div>

        </div>
    </div>
  );
};

export default NodePalette; 